import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import type { CriterioBase } from '@triade/contracts';
import { useAnaliticos } from '@/api/centros-custo';
import { useAtualizarCriterio, useCriarCriterio, useCriterio } from '@/api/criterios-rateio';
import { ErroLista } from '@/components/cargos';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { BASE_ROTULO, BASES, CRITERIO_VAZIO, centroIdsDoCriterio, deCriterio, montarCorpoCriterio, type CriterioFormEntrada } from './criterios-form';
import { ExemploCriterio } from './exemplo-criterio';
import { SelecaoCentros } from './selecao-centros';
import type { CentroEscolha } from './centros-listas';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

export interface BuscaCriterios {
  id?: string;
}

/** Registro de um critério (ou novo). Sem `centro.editar` tudo fica so leitura. */
export function CriterioDetalhe({ id, podeEditar, podeVerCentros }: { id: string | undefined; podeEditar: boolean; podeVerCentros: boolean }) {
  const navigate = useNavigate();
  const doc = useCriterio(id);
  const analiticos = useAnaliticos(false, podeVerCentros);
  const criar = useCriarCriterio();
  const atualizar = useAtualizarCriterio();
  const [erro, setErro] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  const [considerados, setConsiderados] = useState<string[]>([]);
  const [centrosAlterados, setCentrosAlterados] = useState(false);

  const base = useMemo<CriterioFormEntrada>(() => (doc.data ? deCriterio(doc.data) : CRITERIO_VAZIO), [doc.data]);
  const form = useForm<CriterioFormEntrada>({ defaultValues: base, mode: 'onTouched' });
  const { isDirty, errors } = form.formState;
  const todos = form.watch('todosCentros');

  /** Ao carregar (ou trocar de registro), recoloca o formulario e a ordem dos centros gravados. */
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    if (doc.data) setConsiderados(centroIdsDoCriterio(doc.data));
    setCentrosAlterados(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.data]);

  /** Universo da lista dupla: analiticos + centros ja gravados no critério (mesmo que inativos). */
  const opcoes = useMemo<CentroEscolha[]>(() => {
    const mapa = new Map<string, CentroEscolha>();
    for (const a of analiticos.data ?? []) mapa.set(a.id, { id: a.id, rotulo: a.rotulo });
    for (const c of doc.data?.centros ?? []) if (!mapa.has(c.centroId)) mapa.set(c.centroId, { id: c.centroId, rotulo: `${c.caminho} · ${c.nome}` });
    return [...mapa.values()];
  }, [analiticos.data, doc.data]);

  const bloqueio = useBlockerDirty((isDirty || centrosAlterados) && criadoId === null);
  useEffect(() => {
    if (criadoId) void navigate({ to: '/custos/criterios', search: { id: criadoId }, replace: true });
  }, [criadoId, navigate]);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(form.formState.dirtyFields).length + (centrosAlterados ? 1 : 0);

  function apontar(campo: string | undefined, mensagem: string) {
    setErro(mensagem);
    if (campo && ['identificador', 'descricao', 'base'].includes(campo)) {
      form.setError(campo as keyof CriterioFormEntrada, { message: mensagem });
    }
  }

  async function gravar(v: CriterioFormEntrada) {
    setErro(null);
    const montado = montarCorpoCriterio(v, considerados, Boolean(id));
    if (!montado.ok) return apontar(montado.campo, montado.mensagem);
    try {
      if (id) {
        const gravado = await atualizar.mutateAsync({ id, ...montado.corpo });
        form.reset(deCriterio(gravado));
        setConsiderados(centroIdsDoCriterio(gravado));
        setCentrosAlterados(false);
      } else {
        const criado = await criar.mutateAsync(montado.corpo);
        setCriadoId(criado.id);
      }
      setSalvoEm(new Date());
    } catch (e) {
      const mensagem = mensagemApi(e);
      setErro(mensagem);
      if (e instanceof ApiError && e.code === 'CRITERIO_EM_USO') form.setError('base', { message: e.message });
      for (const i of e instanceof ApiError ? (e.body?.issues ?? []) : []) {
        const campo = i.path.split('.')[0] ?? '';
        if (['identificador', 'descricao', 'base'].includes(campo)) form.setError(campo as keyof CriterioFormEntrada, { message: i.message });
      }
    }
  }

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir o critério" /></section>;
  if (id && !doc.data) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando critério…</section>;

  const criterio = doc.data;
  const mostrarExemplo = Boolean(id && criterio);

  return (
    <>
      <div className={cn('grid gap-5', mostrarExemplo && 'lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]')}>
        <section aria-label="Dados do critério" className="rounded-2xl border border-border bg-card p-5">
          <h2 className="text-body font-semibold">{criterio ? criterio.identificador : 'Novo critério'}</h2>
          {!podeEditar && (
            <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
              <b>Somente leitura.</b> Seu acesso permite consultar os critérios, não alterá-los.
            </p>
          )}

          <fieldset disabled={!podeEditar} className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="c-identificador" className={ROTULO}>Identificador</label>
              <input id="c-identificador" maxLength={50} className={CAMPO} aria-invalid={errors.identificador ? true : undefined} aria-describedby={errors.identificador ? 'c-identificador-erro' : undefined} {...form.register('identificador')} />
              <MensagemCampo id="c-identificador-erro" mensagem={errors.identificador?.message} />
            </div>
            <div>
              <label htmlFor="c-base" className={ROTULO}>Base</label>
              <select id="c-base" className={CAMPO} aria-invalid={errors.base ? true : undefined} aria-describedby={errors.base ? 'c-base-erro' : undefined} {...form.register('base')}>
                {BASES.map((b: CriterioBase) => (
                  <option key={b} value={b}>
                    {BASE_ROTULO[b]}
                  </option>
                ))}
              </select>
              <MensagemCampo id="c-base-erro" mensagem={errors.base?.message} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="c-descricao" className={ROTULO}>Descrição</label>
              <input id="c-descricao" maxLength={200} className={CAMPO} aria-invalid={errors.descricao ? true : undefined} aria-describedby={errors.descricao ? 'c-descricao-erro' : undefined} {...form.register('descricao')} />
              <MensagemCampo id="c-descricao-erro" mensagem={errors.descricao?.message} />
            </div>
            <label htmlFor="c-ativo" className="flex cursor-pointer items-center gap-2">
              <input id="c-ativo" type="checkbox" disabled={!id} className="size-[18px] accent-primary" {...form.register('ativo')} />
              Ativo
              {!id && <span className="text-small text-ink-muted">Todo critério novo entra ativo.</span>}
            </label>
          </fieldset>

          <div className="mt-5">
            <label htmlFor="c-todos" className="flex cursor-pointer items-center gap-2 text-small font-medium">
              <input id="c-todos" type="checkbox" disabled={!podeEditar} className="size-[18px] accent-primary" {...form.register('todosCentros', { onChange: () => setCentrosAlterados(true) })} />
              Considerar todos os centros
            </label>
            {!todos && (
              <div className="mt-3">
                {podeVerCentros ? (
                  <SelecaoCentros
                    opcoes={opcoes}
                    considerados={considerados}
                    disabled={!podeEditar}
                    onChange={(lista) => {
                      setConsiderados(lista);
                      setCentrosAlterados(true);
                    }}
                  />
                ) : (
                  <p className="text-small text-ink-muted">Seu acesso não permite ver os centros.</p>
                )}
              </div>
            )}
          </div>

          {erro && (
            <p role="alert" className="mt-4 rounded-xl border border-danger-ink/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
              {erro}
            </p>
          )}
        </section>

        {mostrarExemplo && id && <ExemploCriterio key={id} criterioId={id} />}
      </div>

      {podeEditar && (
        <RecordToolbar
          dirty={isDirty || centrosAlterados}
          saving={salvando}
          savedAt={salvoEm}
          erro={null}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty || centrosAlterados ? alterados : undefined}
          onList={() => void navigate({ to: '/custos/criterios', replace: true })}
          onSave={() => void form.handleSubmit(gravar, () => setErro('Corrija os campos destacados'))()}
          onNew={() => void navigate({ to: '/custos/criterios', search: { id: 'novo' } })}
          onUndo={() => {
            form.reset(base);
            setConsiderados(criterio ? centroIdsDoCriterio(criterio) : []);
            setCentrosAlterados(false);
            setErro(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}
